import { Components, Theme } from '@mui/material/styles';
import '@mui/x-date-pickers/themeAugmentation';

declare module '@mui/material/InputBase' {
  interface InputBaseProps {
    variant?: 'unset';
  }
}

declare module '@mui/material/Input' {
  interface InputProps {
    variant?: 'unset';
  }
}

declare module '@mui/material/OutlinedInput' {
  interface OutlinedInputProps {
    variant?: 'unset';
  }
}

const unsetInputVariantStyles = {
  '&::after, &::before': {
    borderBottom: 'none',
  },
  '&:hover, &:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error)': {
    backgroundColor: 'transparent',
    border: 'none',
    boxShadow: 'none',
  },
  '&.Mui-disabled, &.Mui-error, &.Mui-focused': {
    backgroundColor: 'transparent',
    border: 'none',
    boxShadow: 'none',
  },
  '& .MuiInputBase-input': {
    color: 'inherit',
    font: 'inherit',
    lineHeight: 'inherit',
    padding: 0,
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'inherit',
    font: 'inherit',
    opacity: 0.5,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: 0,
  boxShadow: 'none',
  color: 'inherit',
  font: 'inherit',
  height: 'auto',
  padding: 0,
  transition: 'none',
};

const outlinedFieldRootStyles = (theme: Theme) => {
  const isLight = theme.palette.mode === 'light';
  const ch = isLight ? '0,0,0' : '255,255,255';
  const accentRgb = isLight ? '180,83,9' : '249,186,66';
  const errorRgb = isLight ? '220,38,38' : '231,77,60';
  return {
    ...theme.typography.newF16Medium,
    '&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error)': {
      borderColor: `rgba(${ch},0.14)`,
    },
    '&.Mui-disabled': {
      backgroundColor: `rgba(${ch},0.01)`,
      borderColor: `rgba(${ch},0.04)`,
      color: theme.colors.textMuted,
    },
    '&.Mui-error': {
      '&:hover': {
        borderColor: `rgba(${errorRgb},0.5)`,
      },
      '&.Mui-focused': {
        borderColor: `rgba(${errorRgb},0.7)`,
        boxShadow: `0 0 0 3px rgba(${errorRgb},0.07)`,
      },
      backgroundColor: `rgba(${errorRgb},0.04)`,
      borderColor: `rgba(${errorRgb},0.5)`,
    },
    '&.Mui-focused': {
      backgroundColor: `rgba(${accentRgb},0.04)`,
      borderColor: `rgba(${accentRgb},0.45)`,
      boxShadow: `0 0 0 3px rgba(${accentRgb},0.07)`,
      color: theme.colors.textPrimary,
    },
    backgroundColor: `rgba(${ch},0.03)`,
    border: `1px solid rgba(${ch},0.08)`,
    borderRadius: '6px',
    boxShadow: 'none',
    color: theme.colors.textPrimary,
    height: '36px',
    transition: 'border-color 0.18s, background 0.18s, box-shadow 0.18s',
  };
};

export const components: Components<Omit<Theme, 'components'>> = {
  MuiAvatar: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.newBodyText,
        backgroundColor: theme.colors.bgCard3,
        border: `1px solid ${theme.colors.borderSubtle}`,
        color: theme.colors.textPrimary,
        fontWeight: 450,
      }),
    },
  },
  MuiBadge: {
    styleOverrides: {
      badge: ({ theme }) => ({
        ...theme.typography.f12Regular,
        padding: 0.5,
      }),
    },
  },
  MuiButton: {
    defaultProps: {
      size: 'medium',
      variant: 'contained',
    },
    styleOverrides: {
      root: ({ ownerState, theme }) => {
        const isLight = theme.palette.mode === 'light';
        const ch = isLight ? '0,0,0' : '255,255,255';
        const inkRgb = isLight ? '180,83,9' : '249,186,66';
        return {
          '&:hover': {
            boxShadow: 'none',
          },
          border: '1px solid',
          borderRadius: 50,
          boxShadow: 'none',
          fontSize:
            ownerState.size === 'large' ? '14px' : ownerState.size === 'small' ? '12px' : '13px',
          fontWeight: ownerState.variant === 'contained' ? 600 : 500,
          letterSpacing: 0,
          lineHeight: 1.4,
          textTransform: 'none',
          transition: 'all 0.2s ease',
          ...(ownerState.size === 'large' && {
            padding: '12px 28px',
          }),
          ...(ownerState.size === 'medium' && {
            padding: '10px 24px',
          }),
          ...(ownerState.size === 'small' && {
            padding: '6px 14px',
          }),
          ...(ownerState.variant === 'text' && {
            '&:focus': {
              border: 'none',
            },
            '&:hover': {
              background: 'transparent',
              boxShadow: 'none',
              color: theme.colors.accentInk,
            },
            '&.Mui-disabled': {
              background: 'none',
              borderColor: 'none',
              color: theme.colors.textMuted,
            },
            border: 'none',
            borderColor: 'none',
            color: theme.colors.textSecondary,
            padding: '0',
          }),
          ...(ownerState.variant === 'contained' && {
            '&:focus': {
              background: 'linear-gradient(135deg, #FFD166, #F9BA42)',
              border: 'none',
              boxShadow: '0 0 0 3px rgba(249,186,66,0.15)',
            },
            '&:hover': {
              background: 'linear-gradient(135deg, #FFD166, #F9BA42)',
              boxShadow: '0 6px 18px rgba(249,186,66,0.28)',
              filter: 'brightness(1.05)',
              transform: 'translateY(-1px)',
            },
            '&.Mui-disabled': {
              background: theme.colors.bgCard3,
              border: `1px solid ${theme.colors.borderSubtle}`,
              color: theme.colors.textMuted,
            },
            background: 'linear-gradient(135deg, #F9BA42, #E0A030)',
            border: 'none',
            borderColor: 'none',
            boxShadow: '0 4px 14px rgba(249,186,66,0.20)',
            color: theme.colors.darkCharcoal,
          }),
          ...(ownerState.color === 'warning' &&
            ownerState.variant === 'contained' && {
              '&:focus': {
                background: 'linear-gradient(to bottom, #BF4751, #A1434A)',
                borderColor: '#BF4751',
                boxShadow: 'none',
                filter: 'none',
                transform: 'none',
              },
              '&:hover': {
                background: 'linear-gradient(to bottom, #BF4751, #A1434A)',
                borderColor: '#BF4751',
                boxShadow: 'none',
                filter: 'none',
                transform: 'none',
              },
              background: 'linear-gradient(to bottom, #BF4751, #A1434A)',
              borderColor: '#BF4751',
              boxShadow: 'none',
              color: theme.colors.white,
            }),
          ...(ownerState.variant === 'outlined' && {
            '&:focus': {
              borderColor: `rgba(${inkRgb},0.45)`,
              boxShadow: `0 0 0 3px rgba(${inkRgb},0.07)`,
              color: theme.colors.textPrimary,
            },
            '&:hover': {
              background: `rgba(${ch},0.05)`,
              border: `1px solid rgba(${ch},0.22)`,
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              borderColor: theme.colors.borderSubtle,
              color: theme.colors.textMuted,
            },
            background: 'transparent',
            border: `1px solid rgba(${ch},0.10)`,
            color: theme.colors.textPrimary,
            ...(ownerState.color === 'warning' && {
              '&:hover': {
                border: `1px solid ${theme.colors.darkRed}`,
                borderColor: theme.colors.darkRed,
                boxShadow: 'none',
                color: theme.colors.darkRed,
              },
              border: `1px solid ${theme.colors.red}`,
              borderColor: theme.colors.red,
              color: theme.colors.red,
            }),
            ...(ownerState.color === 'success' && {
              '&:hover': {
                border: `1px solid ${theme.colors.green}`,
                borderColor: theme.colors.green,
                color: theme.colors.green,
              },
              border: `1px solid ${theme.colors.green}`,
              borderColor: theme.colors.green,
              color: theme.colors.green,
            }),
          }),
          ...(ownerState.color === 'success' &&
            ownerState.variant === 'contained' && {
              '&:focus': {
                background: 'linear-gradient(to bottom, #4EC35A, #48A550)',
                boxShadow: 'none',
                color: theme.colors.white,
                filter: 'none',
                transform: 'none',
              },
              '&:hover': {
                background: 'linear-gradient(to bottom, #4EC35A, #48A550)',
                boxShadow: 'none',
                color: theme.colors.white,
                filter: 'none',
                transform: 'none',
              },
              '&.Mui-disabled': {
                borderColor: theme.colors.lightGrey,
                color: theme.colors.lightGrey,
              },
              background: 'linear-gradient(to bottom, #4EC35A, #48A550)',
              boxShadow: 'none',
              color: theme.colors.white,
            }),
          ...(ownerState.color === 'info' && {
            '&:hover': {
              backgroundColor: theme.colors.darkGrey,
              borderColor: theme.colors.darkGrey,
            },
            backgroundColor: theme.colors.grey,
            borderColor: theme.colors.grey,
          }),
        };
      },
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: ({ theme }) => {
        const ch = theme.palette.mode === 'light' ? '0,0,0' : '255,255,255';
        return {
          '&.Mui-checked': {
            color: theme.colors.accentInk,
          },
          '&.Mui-disabled': {
            color: `rgba(${ch},0.08)`,
          },
          '& .MuiSvgIcon-root': {
            fontSize: '16px',
          },
          color: `rgba(${ch},0.20)`,
          padding: 6,
          transition: 'color 0.18s',
        };
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        ...theme.typography.newBodyText,
        '&.MuiChip-outlined': {
          backgroundColor: 'transparent',
          borderColor: theme.colors.borderSubtle,
          color: theme.colors.textPrimary,
        },
        borderRadius: 50,
        ...(ownerState.color === 'success' && {
          background: 'linear-gradient(to bottom, #4EC35A, #48A550)',
          color: theme.colors.white,
        }),
        ...(ownerState.color === 'error' && {
          background: 'linear-gradient(to bottom, #BF4751, #A1434A)',
          color: theme.colors.white,
        }),
        ...(ownerState.color === 'warning' && {
          background: 'linear-gradient(to bottom, #CA7E4A, #A96E46)',
          color: theme.colors.white,
        }),
        ...(ownerState.color === 'info' && {
          background: theme.colors.bgCard2,
          border: `1px solid ${theme.colors.borderSubtle}`,
          borderRadius: 50,
          color: theme.colors.textPrimary,
        }),
        ...(ownerState.color === 'secondary' && {
          '&:has(.MuiChip-icon)': {
            padding: '20px 10px 20px 16px',
          },
          borderRadius: 50,
          padding: '20px 16px',
        }),
      }),
    },
  },
  MuiCssBaseline: {
    styleOverrides: (theme: Omit<Theme, 'components'>) => ({
      '*, *::before, *::after': {
        scrollbarColor: `${theme.colors.borderStrong} transparent`,
        scrollbarWidth: 'thin',
      },
      '*::-webkit-scrollbar': {
        height: 10,
        width: 10,
      },
      '*::-webkit-scrollbar-corner': {
        background: 'transparent',
      },
      '*::-webkit-scrollbar-thumb': {
        backgroundClip: 'content-box',
        backgroundColor: theme.colors.borderStrong,
        border: '2px solid transparent',
        borderRadius: 8,
      },
      '*::-webkit-scrollbar-thumb:active': {
        backgroundColor: theme.colors.accentInk,
      },
      '*::-webkit-scrollbar-thumb:hover': {
        backgroundColor: theme.colors.iconMuted,
      },
      '*::-webkit-scrollbar-track': {
        background: 'transparent',
      },
    }),
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundColor: theme.colors.bgCard,
        backgroundImage: 'none',
        border: `1px solid ${theme.colors.stroke}`,
        borderRadius: '14px',
        boxShadow: theme.colors.shadowCard,
      }),
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderTop: `1px solid ${theme.colors.stroke}`,
        gap: theme.spacing(1),
        padding: theme.spacing(2, 3),
      }),
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        '.MuiDialogTitle-root + &': {
          paddingTop: theme.spacing(3),
        },
        paddingTop: theme.spacing(3),
      }),
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.newBodyText,
        borderBottom: `1px solid ${theme.colors.stroke}`,
        color: theme.colors.textPrimary,
        fontWeight: 600,
        padding: theme.spacing(3),
      }),
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: theme.colors.borderSubtle,
        marginBottom: 16,
        marginTop: 16,
      }),
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      asterisk: ({ theme }) => ({
        color: theme.colors.red,
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        '&:active': {
          backgroundColor: theme.colors.bgCard3,
        },
        '&:hover': {
          backgroundColor: theme.colors.bgCard3,
          color: theme.colors.textPrimary,
        },
        '&.Mui-disabled': {
          color: theme.colors.textMuted,
        },
        color: theme.colors.iconMuted,
        transition: 'color 0.2s, background-color 0.2s',
        ...(ownerState.size === 'small' && {
          '&:hover': {
            backgroundColor: theme.colors.bgHover,
            borderColor: theme.colors.border,
            color: theme.colors.textPrimary,
          },
          border: `1px solid ${theme.colors.borderSubtle}`,
          borderRadius: '8px',
          height: 32,
          width: 32,
        }),
      }),
    },
  },
  MuiInput: {
    styleOverrides: {
      input: ({ theme }) => ({
        '&::placeholder': {
          ...theme.typography.newF16Medium,
          color: theme.colors.iconMuted,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        padding: 0,
      }),
      root: ({ theme }) => {
        const isLight = theme.palette.mode === 'light';
        const ch = isLight ? '0,0,0' : '255,255,255';
        const accentRgb = isLight ? '180,83,9' : '249,186,66';
        const errorRgb = isLight ? '220,38,38' : '231,77,60';
        return {
          ...theme.typography.newF16Medium,
          '&::before': {
            borderBottom: 'none',
          },
          '&:hover:not(.Mui-disabled, .Mui-error)': {
            '&::before': {
              borderBottom: 'none',
            },
          },
          '&:hover:not(.Mui-focused):not(.Mui-error)': {
            borderColor: `rgba(${ch},0.14)`,
          },
          '&.Mui-disabled': {
            '&::before': {
              borderBottom: 'none',
            },
            backgroundColor: `rgba(${ch},0.01)`,
            borderColor: `rgba(${ch},0.04)`,
            color: theme.colors.textMuted,
          },
          '&.Mui-error': {
            '&:hover': {
              borderColor: `rgba(${errorRgb},0.5)`,
            },
            '&.Mui-focused': {
              borderColor: `rgba(${errorRgb},0.7)`,
              boxShadow: `0 0 0 3px rgba(${errorRgb},0.07)`,
            },
            borderColor: `rgba(${errorRgb},0.5)`,
          },
          '&.Mui-focused': {
            backgroundColor: `rgba(${accentRgb},0.04)`,
            borderColor: `rgba(${accentRgb},0.45)`,
            boxShadow: `0 0 0 3px rgba(${accentRgb},0.07)`,
          },
          backgroundColor: `rgba(${ch},0.03)`,
          border: `1px solid rgba(${ch},0.08)`,
          borderRadius: '6px',
          boxShadow: 'none',
          color: theme.colors.textPrimary,
          padding: '0 12px',
          transition: 'border-color 0.18s, background 0.18s, box-shadow 0.18s',
        };
      },
    },
    variants: [
      {
        props: { variant: 'unset' },
        style: unsetInputVariantStyles,
      },
    ],
  },
  MuiInputBase: {
    styleOverrides: {
      input: ({ theme }) => ({
        '&.MuiInput-input': {
          '&.Mui-disabled': {
            '-webkit-text-fill-color': theme.colors.grey,
          },
        },
        '&[data-testid="textareaAutosize"], &.MuiInputBase-inputMultiline': {
          minHeight: 'unset',
        },
      }),
      root: ({ theme }) => {
        const isLight = theme.palette.mode === 'light';
        const ch = isLight ? '0,0,0' : '255,255,255';
        const errorRgb = isLight ? '220,38,38' : '231,77,60';
        return {
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              '&:hover': {
                border: 'none',
              },
              border: 'none',
            },
          },
          '&.MuiInput-root': {
            '&.Mui-disabled': {
              backgroundColor: `rgba(${ch},0.01)`,
              borderColor: `rgba(${ch},0.04)`,
            },
            '&.Mui-error': {
              borderColor: `rgba(${errorRgb},0.5)`,
            },
          },
          '&.MuiInputBase-multiline': {
            height: 'auto',
            minHeight: '80px',
          },
          '&.MuiOutlinedInput-root': {
            '&.Mui-disabled': {
              backgroundColor: `rgba(${ch},0.01)`,
              borderColor: `rgba(${ch},0.04)`,
            },
            '&.Mui-error': {
              borderColor: `rgba(${errorRgb},0.5)`,
            },
          },
          height: '36px',
        };
      },
    },
    variants: [
      {
        props: { variant: 'unset' },
        style: unsetInputVariantStyles,
      },
    ],
  },
  MuiInputLabel: {
    styleOverrides: {
      asterisk: ({ theme }) => ({
        color: theme.colors.red,
      }),
      root: ({ theme }) => ({
        ...theme.typography.f12Regular,
        '&.Mui-disabled': {
          color: theme.colors.textMuted,
        },
        '&.Mui-error': {
          color: theme.colors.red,
        },
        '&.Mui-focused': {
          color: theme.colors.textSecondary,
        },
        color: theme.colors.textSecondary,
        fontWeight: 700,
        letterSpacing: '0.6px',
        marginBottom: 6,
        position: 'relative',
        transform: 'none',
      }),
    },
  },
  MuiMenu: {
    styleOverrides: {
      list: {
        padding: 4,
      },
      paper: ({ theme }) => ({
        background: theme.colors.bgCard,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: 12,
        boxShadow: theme.colors.shadowCard,
        color: theme.colors.textPrimary,
        marginTop: 6,
      }),
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.newBodyText,
        '&:hover': {
          background: theme.colors.bgHover,
        },
        '&.Mui-selected': {
          '&:hover': {
            background: theme.colors.accentBg,
          },
          background: theme.colors.accentBg,
          color: theme.colors.textPrimary,
        },
        borderRadius: 8,
        color: theme.colors.textPrimary,
        margin: '2px 0',
        minHeight: 38,
      }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: ({ theme }) => ({
        '&::placeholder': {
          ...theme.typography.newF16Medium,
          color: theme.colors.iconMuted,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        padding: '0 12px',
      }),
      notchedOutline: {
        border: 'none',
      },
      root: ({ theme }) => {
        const isLight = theme.palette.mode === 'light';
        const ch = isLight ? '0,0,0' : '255,255,255';
        const accentRgb = isLight ? '180,83,9' : '249,186,66';
        const errorRgb = isLight ? '220,38,38' : '231,77,60';
        return {
          ...theme.typography.newF16Medium,
          '&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error)': {
            borderColor: `rgba(${ch},0.14)`,
          },
          '&.Mui-disabled': {
            backgroundColor: `rgba(${ch},0.01)`,
            borderColor: `rgba(${ch},0.04)`,
            color: theme.colors.textMuted,
          },
          '&.Mui-error': {
            '&:hover': {
              borderColor: `rgba(${errorRgb},0.5)`,
            },
            '&.Mui-focused': {
              borderColor: `rgba(${errorRgb},0.7)`,
              boxShadow: `0 0 0 3px rgba(${errorRgb},0.07)`,
            },
            backgroundColor: `rgba(${errorRgb},0.04)`,
            borderColor: `rgba(${errorRgb},0.5)`,
          },
          '&.Mui-focused': {
            backgroundColor: `rgba(${accentRgb},0.04)`,
            borderColor: `rgba(${accentRgb},0.45)`,
            boxShadow: `0 0 0 3px rgba(${accentRgb},0.07)`,
            color: theme.colors.textPrimary,
          },
          backgroundColor: `rgba(${ch},0.03)`,
          border: `1px solid rgba(${ch},0.08)`,
          borderRadius: '6px',
          boxShadow: 'none',
          color: theme.colors.textPrimary,
          height: '36px',
          padding: 0,
          transition: 'border-color 0.18s, background 0.18s, box-shadow 0.18s',
        };
      },
    },
    variants: [
      {
        props: { variant: 'unset' },
        style: unsetInputVariantStyles,
      },
    ],
  },
  MuiPaper: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.MuiAccordion-root': {
          '&::before': {
            height: 0,
          },
          '&.Mui-expanded': {
            margin: 0,
          },
          border: `2px solid ${theme.colors.borderSubtle}`,
          borderRadius: '20px !important',
        },
        boxShadow: theme.colors.shadowCard,
      }),
    },
  },
  MuiPickersInputBase: {
    styleOverrides: {
      root: ({ theme }) => outlinedFieldRootStyles(theme),
      sectionsContainer: {
        padding: '0 12px',
      },
    },
  },
  MuiPickersOutlinedInput: {
    styleOverrides: {
      notchedOutline: {
        border: 'none',
      },
      root: ({ theme }) => ({
        ...outlinedFieldRootStyles(theme),
        '& .MuiPickersSectionList-sectionContent[aria-valuetext="Empty"]': {
          color: theme.colors.iconMuted,
          opacity: 0.42,
        },
        padding: 0,
      }),
      sectionsContainer: {
        padding: '0 12px',
      },
    },
  },
  MuiPickersSectionList: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.newF16Medium,
        color: theme.colors.textPrimary,
      }),
    },
  },
  MuiSelect: {
    styleOverrides: {
      icon: ({ theme }) => ({
        color: theme.colors.iconMuted,
        right: 12,
      }),
      select: ({ theme }) => ({
        ...theme.typography.newF16Medium,
        '&:focus': {
          background: 'transparent',
        },
        alignItems: 'center',
        color: theme.colors.textPrimary,
        display: 'flex',
        minHeight: 'unset',
        paddingBottom: 0,
        paddingTop: 0,
      }),
    },
  },
  MuiSlider: {
    styleOverrides: {
      rail: ({ theme }) => ({
        backgroundColor: theme.colors.lightGrey,
        borderRadius: 4,
        height: 4,
      }),
      root: {
        color: 'transparent',
      },
      thumb: ({ theme }) => ({
        '&:hover': {
          boxShadow: 'none',
        },
        backgroundColor: theme.colors.red,
        border: `2px solid ${theme.colors.white}`,
        height: 16,
        width: 16,
      }),
      track: ({ theme }) => ({
        backgroundColor: theme.colors.lightGrey,
        borderRadius: 4,
        height: 4,
      }),
      valueLabel: ({ theme }) => ({
        background: 'none',
        color: theme.colors.darkGrey,
        top: 0,
      }),
    },
  },
  MuiSvgIcon: {
    styleOverrides: {
      root: {
        fontSize: '20px',
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.f14Regular,
        '&.Mui-selected': {
          border: `1px solid ${theme.colors.scarletRed}`,
          color: theme.colors.scarletRed,
        },
        border: 'none',
        borderRadius: 8,
        color: theme.colors.textSecondary,
        padding: '8px 32px',
      }),
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.f14Regular,
        '&.MuiTableCell-head': {
          color: theme.colors.grey,
        },
        // '&.MuiTableCell-body': {
        //   '&:first-of-type': {
        //     color: theme.colors.turquoise,
        //   },
        // },
      }),
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      select: {
        paddingRight: '28px !important',
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        '& .MuiTabs-flexContainer': {
          paddingBottom: '2px',
        },
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:hover': {
          backgroundColor:
            theme.palette.mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)',
          borderColor: theme.colors.border,
          color: theme.colors.textPrimary,
        },
        '&.Mui-disabled': {
          borderColor: theme.colors.borderSubtle,
          color: theme.colors.textMuted,
        },
        '&.Mui-selected': {
          '&:hover': {
            backgroundColor: theme.colors.accentBg,
            borderColor: theme.colors.accentInk,
            color: theme.colors.accentInk,
          },
          backgroundColor: theme.colors.accentBg,
          borderColor: theme.colors.accentInk,
          color: theme.colors.accentInk,
        },
        backgroundColor: 'transparent',
        border: `1px solid ${theme.colors.borderSubtle}`,
        borderRadius: '20px',
        color: theme.colors.textSecondary,
        fontSize: '12px',
        fontWeight: 500,
        padding: '6px 14px',
        textTransform: 'none',
        transition: 'all 0.2s ease',
      }),
    },
  },
};
